import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import { type HotkeyDefinition } from "./types";

interface HotkeyContextValue {
  register: (hotkey: HotkeyDefinition) => () => void;
  getActiveHotkeys: () => HotkeyDefinition[];
}

const HotkeyContext = createContext<HotkeyContextValue | null>(null);

export const HotkeyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Use a ref for the map to avoid re-renders on every register/unregister,
  // and a counter to trigger re-reads only when the modal actually needs them.
  const hotkeysRef = useRef(new Map<string, HotkeyDefinition>());
  const [, setVersion] = useState(0);

  const register = useCallback((hotkey: HotkeyDefinition) => {
    hotkeysRef.current.set(hotkey.id, hotkey);
    setVersion((v) => v + 1);
    return () => {
      hotkeysRef.current.delete(hotkey.id);
      setVersion((v) => v + 1);
    };
  }, []);

  const getActiveHotkeys = useCallback(
    () => Array.from(hotkeysRef.current.values()),
    []
  );

  const value = useMemo(
    () => ({ register, getActiveHotkeys }),
    [register, getActiveHotkeys]
  );

  return (
    <HotkeyContext.Provider value={value}>{children}</HotkeyContext.Provider>
  );
};

export const useHotkeyContext = () => {
  const ctx = useContext(HotkeyContext);
  if (!ctx)
    throw new Error("useHotkeyContext must be used within HotkeyProvider");
  return ctx;
};
